import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Stack, Text } from '@chakra-ui/layout';
import { useHistory } from 'react-router-dom';
import { FiTrash } from 'react-icons/all';
import { FiDownload } from 'react-icons/fi';
import download from 'downloadjs';
import { Button, Heading, Pagination, Table } from '~/components';
import style from './style.module.css';
import { useZeroCarbonProjects } from '~/hooks/useZeroCarbon/useProjects';
import { ROUTER_PATHS } from '~/constants';
import {
  deleteProjectInvoice,
  deleteZeroCarbonProjet,
  downloadProjectFile,
  downloadProjectInvoiceFile,
  getProjectInvoice,
  getProjectsXlsx,
  getZeroCarbonProjectById,
} from '~/services/ZeroCarbon';
import {
  ZeroCarbonProjectInvoiceView,
  ZeroCarbonProjectView,
} from '~/types/zeroCarbon';
import { NewProjectInvoiceDialog } from '~/pages/Portal/Admin/ZeroCarbon/ZeroCarbonProjects/newProjectInvoiceDialog';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { queryClient } from '~/utils/react-query';

export const ZeroCarbonProjects: React.VFC = () => {
  const history = useHistory();
  const toast = useToast();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [page, setPage] = useState(1);
  const [listaProjetos, setListaProjetos] = useState<ZeroCarbonProjectView[]>(
    [],
  );
  const [notaSelecionada, setNotaSelecionada] = useState<
    ZeroCarbonProjectInvoiceView | undefined
  >(undefined);
  const [projetoSelecionado, setProjetoSelecionado] = useState<
    ZeroCarbonProjectView | undefined
  >(undefined);
  const [isFetchingRemover, setIsFetchingRemover] = useState(false);
  const { data, isLoading, refetch } = useZeroCarbonProjects(page, 10, {
    onSuccess: r => {
      setListaProjetos(r?.results || []);
    },
  });
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  useEffect(() => {
    refetch();
  }, []);

  const carregarNotasFiscais = (
    projeto: ZeroCarbonProjectView,
    index: number,
    carregar?: boolean,
  ) => {
    if (
      listaProjetos[index].collapsed &&
      !!listaProjetos[index].notas_fiscais?.length &&
      !carregar
    ) {
      return;
    }
    const p = listaProjetos;
    p[index].loading = true;
    setListaProjetos([...p]);
    getProjectInvoice(projeto.id)
      .then(response => {
        const projetos = listaProjetos;
        projetos[index].notas_fiscais = response;
        projetos[index].collapsed = !projetos[index].collapsed;
        setListaProjetos([...projetos]);
      })
      .finally(() => {
        const projetos = listaProjetos;
        projetos[index].loading = false;
        setListaProjetos([...projetos]);
      });
  };

  const carregarProjeto = (projeto: ZeroCarbonProjectView) => {
    getZeroCarbonProjectById(projeto.id).then(response => {
      const projetos = listaProjetos;
      const index = listaProjetos.indexOf(projeto);
      projetos[index].formattedQuantidadeCo2Contratado =
        response.formattedQuantidadeCo2Contratado;
      projetos[index].formattedQuantidadeCo2Disponivel =
        response.formattedQuantidadeCo2Disponivel;
      projetos[index].formattedQuantidadeCo2Utilizado =
        response.formattedQuantidadeCo2Utilizado;
      setListaProjetos([...projetos]);
    });
  };

  const removerProjeto = (id: number) => {
    setIsFetchingRemover(true);
    deleteZeroCarbonProjet(id)
      .then(() => {
        toast({
          title: 'Projeto removido com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        onCloseModal();
        setProjetoSelecionado(undefined);
        if (listaProjetos.length === 1 && page > 1) {
          setPage(page - 1);
          queryClient.invalidateQueries(['zero_carbon_projects', page - 1]);
        } else {
          queryClient.invalidateQueries(['zero_carbon_projects', page]);
        }
      })
      .catch(err => {
        toast({
          title: 'Erro ao remover projeto',
          description: err?.response?.data?.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingRemover(false);
      });
  };

  const removerNotaFiscal = (idProjeto: number, idNota: number) => {
    setIsFetchingRemover(true);
    deleteProjectInvoice(idNota, idProjeto)
      .then(() => {
        toast({
          title: 'Nota fiscal removida com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries(['zero_carbon_projects']);
        if (projetoSelecionado) {
          carregarNotasFiscais(
            projetoSelecionado,
            listaProjetos.indexOf(projetoSelecionado),
            true,
          );
          carregarProjeto(projetoSelecionado);
        }
        setProjetoSelecionado(undefined);
        setNotaSelecionada(undefined);
        onCloseModal();
      })
      .catch(err => {
        toast({
          title: 'Erro ao remover nota fiscal',
          description: err?.response?.data?.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingRemover(false);
      });
  };

  const downloadArquivo = (idProjeto: number, idNota?: number) => {
    let response;
    if (idNota) {
      response = downloadProjectInvoiceFile(idNota, idProjeto);
    } else {
      response = downloadProjectFile(idProjeto);
    }
    response
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.message,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };
  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getProjectsXlsx()
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        const error2 = JSON.parse(await err.response.data.text());
        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  return (
    <Box px="8" py="4">
      <Stack gridGap={4}>
        <HStack justifyContent="space-between">
          <Heading>Projetos Zero Carbon</Heading>
          <HStack
            gridGap={2}
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Button
              disabled={
                isLoadingDownload || isLoading || listaProjetos.length === 0
              }
              onClick={handleDownload}
              color="black"
              background="transparent"
              border="1px solid black"
              style={{ marginTop: '20px' }}
            >
              Exportar
              {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                history.push(
                  ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_CREATE_PROJECT,
                )
              }
            >
              Cadastrar projeto
            </Button>
          </HStack>
        </HStack>
        <TableContainer>
          <Table type="secondary">
            <Thead>
              <Tr>
                <Th w="15%" maxW="15%">
                  NOME DO PROJETO
                </Th>
                <Th w="15%" maxW="15%" whiteSpace="pre-wrap">
                  QTDE DE CO2E ADQUIRIDA
                </Th>
                <Th w="15%" maxW="15%" whiteSpace="pre-wrap">
                  QTDE DE CO2E UTILIZADA
                </Th>
                <Th w="15%" maxW="15%" whiteSpace="pre-wrap">
                  QTDE DE CO2E DISPONÍVEL
                </Th>
                <Th w="15%" maxW="15%">
                  VALIDADORA
                </Th>
                <Th w="15%" maxW="15%">
                  E-MAIL
                </Th>
                <Th w="10%" maxW="10%" textAlign="center">
                  AÇÕES
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {listaProjetos.length ? (
                listaProjetos.map((projeto, index) => (
                  <Tr key={projeto.id}>
                    <Td className={style.td} colSpan={7}>
                      <Accordion allowMultiple>
                        <AccordionItem id={projeto.id.toString()}>
                          <h2>
                            <AccordionButton
                              p="0px"
                              onClick={() =>
                                carregarNotasFiscais(projeto, index)
                              }
                            >
                              <Table
                                type="secondary"
                                borderRadius="0px !important"
                              >
                                <Tbody>
                                  <Tr>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      <AccordionIcon boxSize="20px" />{' '}
                                      {projeto.nome}
                                    </Td>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      {projeto.formattedQuantidadeCo2Contratado}{' '}
                                      tCO2e
                                    </Td>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      {projeto.formattedQuantidadeCo2Utilizado}{' '}
                                      tCO2e
                                    </Td>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      {projeto.formattedQuantidadeCo2Disponivel}{' '}
                                      tCO2e
                                    </Td>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      {projeto.validadora}
                                    </Td>
                                    <Td
                                      border={0}
                                      w="15%"
                                      maxW="15%"
                                      whiteSpace="pre-wrap"
                                    >
                                      {projeto.email}
                                    </Td>
                                    <Td
                                      border={0}
                                      w="10%"
                                      maxW="10%"
                                      textAlign="center"
                                    >
                                      <NewProjectInvoiceDialog
                                        projeto={projeto}
                                        carregarNotasFiscais={() => {
                                          carregarNotasFiscais(
                                            projeto,
                                            index,
                                            true,
                                          );
                                          carregarProjeto(projeto);
                                        }}
                                      />
                                      <Tooltip
                                        hasArrow
                                        label="Download de Projeto"
                                        bg="black"
                                      >
                                        <IconButton
                                          me={1}
                                          size="sm"
                                          aria-label="Download"
                                          icon={<Icon as={FiDownload} />}
                                          onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            downloadArquivo(projeto.id);
                                          }}
                                        />
                                      </Tooltip>
                                      <Tooltip
                                        hasArrow
                                        label="Remover projeto"
                                        bg="black"
                                      >
                                        <IconButton
                                          size="sm"
                                          aria-label="Remover projeto"
                                          icon={<Icon as={FiTrash} />}
                                          onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setProjetoSelecionado(projeto);
                                            onOpenModal();
                                          }}
                                        />
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </AccordionButton>
                          </h2>
                          <AccordionPanel p={0}>
                            <Table type="primary">
                              <Thead>
                                <Tr color="#8A909C !important">
                                  <Th
                                    ps="24px !important"
                                    py="0.5rem !important"
                                    color="#8A909C !important"
                                  >
                                    N° NOTA FISCAL
                                  </Th>
                                  <Th
                                    py="0.5rem !important"
                                    color="#8A909C !important"
                                  >
                                    DATA DA COMPRA
                                  </Th>
                                  <Th
                                    py="0.5rem !important"
                                    color="#8A909C !important"
                                  >
                                    VALOR
                                  </Th>
                                  <Th
                                    py="0.5rem !important"
                                    color="#8A909C !important"
                                  >
                                    QTDE C02E
                                  </Th>
                                  <Th
                                    py="0.5rem !important"
                                    pe="24px !important"
                                    color="#8A909C !important"
                                    style={{ textAlign: 'center' }}
                                  >
                                    AÇÕES
                                  </Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {projeto.notas_fiscais?.length ? (
                                  projeto.notas_fiscais?.map(nota => (
                                    <Tr key={nota.id!}>
                                      <Td ps="24px !important">
                                        {nota.numero}
                                      </Td>
                                      <Td>{nota.formattedDataCompra}</Td>
                                      <Td>{nota.formattedValor}</Td>
                                      <Td>
                                        {nota.formattedQuantidadeCo2Contratado}t
                                      </Td>
                                      <Td
                                        pe="24px !important"
                                        display="flex"
                                        justifyContent="center"
                                        gridGap={3}
                                      >
                                        <Tooltip
                                          hasArrow
                                          label="Download de Nota Fiscal"
                                          bg="black"
                                        >
                                          <IconButton
                                            size="sm"
                                            aria-label="Download nota fiscal"
                                            icon={<Icon as={FiDownload} />}
                                            onClick={() =>
                                              downloadArquivo(
                                                projeto.id,
                                                nota.id,
                                              )
                                            }
                                          />
                                        </Tooltip>
                                        <Tooltip
                                          hasArrow
                                          label="Remover nota fiscal"
                                          bg="black"
                                        >
                                          <IconButton
                                            size="sm"
                                            aria-label="Remover nota fiscal"
                                            icon={<Icon as={FiTrash} />}
                                            onClick={() => {
                                              setNotaSelecionada(nota);
                                              setProjetoSelecionado(projeto);
                                              onOpenModal();
                                            }}
                                          />
                                        </Tooltip>
                                      </Td>
                                    </Tr>
                                  ))
                                ) : (
                                  <Tr>
                                    <Td
                                      colSpan={5}
                                      style={{ textAlign: 'center' }}
                                    >
                                      {projeto.loading && <Spinner />}
                                      {!projeto.loading && (
                                        <Text>
                                          Nenhuma nota fiscal encontrada
                                        </Text>
                                      )}
                                    </Td>
                                  </Tr>
                                )}
                              </Tbody>
                            </Table>
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center">
                    {isLoading && <Spinner />}
                    {!isLoading && <Text>Nenhum projeto encontrado</Text>}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Flex justify="flex-end" height="fit-content">
          {data && (
            <Pagination
              marginTop="0px"
              totalCountOfRegisters={data.count}
              registersPerPage={10}
              currentPage={page}
              onPageChange={e => {
                setPage(e);
                queryClient.invalidateQueries(['zero_carbon_projects', e]);
              }}
            />
          )}
        </Flex>
      </Stack>
      <ConfirmationDialog
        confirmar={() => {
          if (notaSelecionada) {
            removerNotaFiscal(projetoSelecionado!.id!, notaSelecionada.id!);
          } else {
            removerProjeto(projetoSelecionado!.id!);
          }
        }}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo={notaSelecionada ? 'Remover nota fiscal' : 'Remover projeto'}
        btnConfirmText="Confirmar"
        isDisabled={isFetchingRemover}
      >
        {notaSelecionada ? (
          <span>
            Tem certeza que deseja remover a nota fiscal{' '}
            <b>{notaSelecionada.numero}</b>?
          </span>
        ) : (
          <span>
            Tem certeza que deseja remover o projeto{' '}
            <b>{projetoSelecionado?.nome}</b>?
          </span>
        )}
      </ConfirmationDialog>
    </Box>
  );
};
